import { useMemo, useState } from 'react'
import { Picker } from '../Picker'
import { Dialog } from '../Dialog'
import { GuideLink } from '../guides/GuideLink'
import { SearchIcon } from '../icons'
import { messageOf } from '../../lib/errors'
import {
  setReorderLevel,
  setStock,
  sortRows,
  stockRefusal,
  transferStock,
  variantMatches,
  type ReorderAlert,
  type RoomInventoryRow,
  type ShelfSort,
  type StockRoom,
} from '../../lib/shop'

/** REC-65's quick filters over the shelf table. */
export type ShelfShow = 'all' | 'low' | 'waiting'

/**
 * WHAT IS ON THE SHELF. David's stock room management, 074, widened by 075.
 *
 * ── THE AXES ARE COLUMNS, AND THAT IS WHAT 068's `kind` WAS FOR ─────────────
 *
 * "Navy · M · Men's" in one cell cannot be scanned down, filtered, or compared
 * between two items. Size, colour and fit each get a column — possible only
 * because 068b refuses a second axis of one standard kind per item, which was
 * written as a correctness fix for the cross product and turns out to be
 * load-bearing here.
 *
 * **Custom axes share ONE column, and it carries the axis NAME** — David's "use
 * the other name and variant name as the field". A custom axis has no identity
 * across items (the shirt's "Sleeve" and a mug's "Handle" are unrelated) and an
 * item may declare several, so a column each would be a table whose shape
 * changed with whichever items happened to be on this shelf.
 *
 * The rendered label stays as the first cell rather than being replaced by the
 * columns. That redundancy is deliberate: the label is what `shop_order`
 * snapshots and what a buyer saw in the Store, so a manager has to be able to
 * read the same words; the columns are for scanning and filtering.
 *
 * ── THE FILTERS ARE CLIENT-SIDE, AND THE DISTINCTION MATTERS ────────────────
 *
 * "`p_limit` is applied by the database" has been written down seven times and
 * does not apply here: `stock_room_inventory` takes no limit, so this component
 * holds every row of the rooms it was given and narrowing them cannot silently
 * drop matches. The ROOMS go to the server, because that is what decides which
 * rows exist at all — and a room the caller does not manage refuses the whole
 * call rather than quietly shrinking the list (043).
 *
 * ── AND THE COUNT IS STILL AN OBSERVATION ───────────────────────────────────
 *
 * No +1 anywhere. 065's rule: a delta is how two managers counting one shelf
 * produce a number neither of them saw.
 */
export function RoomStock({
  rooms,
  roomIds,
  onPickRooms,
  rows,
  alerts,
  error,
  onCreateItem,
  onAddStock,
  onError,
  onChanged,
  help,
  initialItemFilter = [],
  initialShow = 'all',
}: {
  /**
   * REC-65: which quick filter starts on. Controlled from outside only so the
   * suite can render the filtered table — a filter that only ever turns on by a
   * click is one no assertion can reach (062).
   */
  initialShow?: ShelfShow
  /**
   * REC-65: the item tray's "Open on the Stock tab" arrives with the item
   * filter set. Initial only — the Picker owns it after that.
   */
  initialItemFilter?: string[]
  rooms: StockRoom[]
  /** Several. A manager with Lima and Santiago counts them in one table. */
  roomIds: string[]
  onPickRooms: (ids: string[]) => void
  /** Null while the shelves are being read. Empty is a genuinely bare shelf. */
  rows: RoomInventoryRow[] | null
  alerts: ReorderAlert[]
  /**
   * SHOWN, never swallowed into an empty state — an empty shelf and a shelf you
   * may not read look identical otherwise, which is the 042 failure.
   */
  error: string | null
  onCreateItem: () => void
  onAddStock: () => void
  onError: (m: string | null) => void
  onChanged: () => void
  /** The "How counting works" link, drawn beside the two buttons (14 Oct). */
  help?: React.ReactNode
}) {
  const [itemFilter, setItemFilter] = useState<string[]>(initialItemFilter)
  const [variantQuery, setVariantQuery] = useState('')
  const [sortBy, setSortBy] = useState<ShelfSort>('item')
  const [show, setShow] = useState<ShelfShow>(initialShow)
  /** Which row is being moved to another store. One at a time. */
  const [moving, setMoving] = useState<RoomInventoryRow | null>(null)

  // Only rooms this person can act on. 075 REFUSES a room you do not run — and
  // refuses the whole call, not just that room — so offering the others puts a
  // guaranteed error one click away.
  const mine = useMemo(() => rooms.filter((r) => r.iManage), [rooms])

  // Built from the rows on screen, so the filter can only ever offer something
  // that is actually there. An item list from the catalogue would let somebody
  // filter to a row that does not exist and read the empty result as a fault.
  const itemOptions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const r of rows ?? []) if (!seen.has(r.itemId)) seen.set(r.itemId, r.itemName)
    return [...seen].map(([value, label]) => ({ value, label }))
  }, [rows])

  const shown = useMemo(() => {
    const chosen = new Set(itemFilter)
    const kept = (rows ?? []).filter(
      (r) =>
        (chosen.size === 0 || chosen.has(r.itemId)) &&
        variantMatches(r, variantQuery) &&
        (show === 'all' ||
          (show === 'low' && r.needsReorder && r.itemIsActive) ||
          (show === 'waiting' && r.awaiting > 0)),
    )
    return sortRows(kept, sortBy)
  }, [rows, itemFilter, variantQuery, sortBy, show])

  const waitingLines = (rows ?? []).filter((r) => r.awaiting > 0).length

  /**
   * GROUPING IS ONLY HONEST WHEN THE SORT IS BY ITEM.
   *
   * Grouping by item IS an ordering by item, so grouping while sorted by colour
   * would have to either break the colour order or scatter each item's rows —
   * one of the two claims on screen would be a lie. So the table groups when
   * that is what the sort says and goes flat otherwise, with the item name on
   * every row instead of a heading above them.
   */
  const grouped = sortBy === 'item'

  const groups = useMemo(() => {
    if (!grouped) return []
    const out: { itemId: string; name: string; code: string; active: boolean; lines: RoomInventoryRow[] }[] = []
    for (const r of shown) {
      const last = out[out.length - 1]
      if (last && last.itemId === r.itemId) last.lines.push(r)
      else out.push({ itemId: r.itemId, name: r.itemName, code: r.itemCode, active: r.itemIsActive, lines: [r] })
    }
    return out
  }, [shown, grouped])

  const low = shown.filter((r) => r.needsReorder).length
  const filtered = rows !== null && shown.length !== rows.length

  return (
    <div className="roomstock">
      {error && <p className="roles__error" role="alert">{error}</p>}

      {/* ── ROW 1: what is on screen, and the two acts (14 Oct) ─────────────
          People and roles' shape: the count on the left, the buttons at the
          far end, no card around the lot. The count used to be a caption
          above the table, which put it below the filters it was counting. */}
      <div className="people__bar roomstock__head">
        <p className="people__count" aria-live="polite">
          {rows !== null && shown.length > 0 && (
            <>
              <strong>
                {grouped
                  ? `${groups.length} ${groups.length === 1 ? 'item' : 'items'}`
                  : `Sorted by ${sortBy}`}
              </strong>
              <span className="people__countnote">
                {' · '}{shown.length} {shown.length === 1 ? 'line' : 'lines'}
                {filtered && ` of ${rows.length}`}
                {low > 0 && ` · ${low} below level`}
              </span>
            </>
          )}
        </p>
        {/* TWO BUTTONS, TWO ACTS. Creating an item is a catalogue decision made
            once; putting boxes on a shelf happens every delivery. */}
        <div className="people__actions roomstock__actions">
          {help}
          {/* 353 (HLP-50): Add item stock is what a delivery is. */}
          <GuideLink guide="store-manager" section="delivery" label="How a delivery works" />
          <button type="button" className="btn btn--sm" onClick={onCreateItem}>
            Create an item
          </button>
          <button type="button" className="btn btn--primary btn--sm" onClick={onAddStock}>
            Add item stock
          </button>
        </div>
      </div>

      {/* ── ROW 2: search, the pickers, the sort, the quick filters ────────
          The same pill recipe as People and roles and Star One admin, so a
          filter looks like a filter on every admin screen. They were labelled
          form fields of four different shapes (two picker carets, a native
          chevron, labels in two type sizes), which is what read as misaligned. */}
      <div className="people__filters roomstock__filters" role="search" aria-label="Filter the shelf">
        <label className="people__search">
          <span className="searchwrap__icon" aria-hidden="true">
            <SearchIcon size={15} />
          </span>
          <span className="visually-hidden">Variant</span>
          <input
            type="search"
            value={variantQuery}
            // Two words, because that is what people type. Every term has to
            // match something — see variantMatches.
            placeholder="Find a variant: m grey, navy long…"
            autoComplete="off"
            onChange={(e) => setVariantQuery(e.target.value)}
          />
        </label>

        {/* SERVER-SIDE, and multi. The Picker rather than a select for 046's
            reason: there is no ctrl key on a tablet, and a mis-click on a
            native multi-select clears everything else with no undo. */}
        <Picker
          variant="pill"
          label="Stores"
          anyLabel="Every store you run"
          searchPlaceholder="Search stores"
          options={mine.map((r) => ({
            value: r.id,
            label: r.isActive ? r.name : `${r.name} (closed)`,
            hint: r.city,
          }))}
          selected={roomIds}
          onChange={onPickRooms}
        />

        {/* CLIENT-SIDE, and honest because the read has no limit — see header. */}
        <Picker
          variant="pill"
          label="Items"
          anyLabel="Every item"
          searchPlaceholder="Search items"
          options={itemOptions}
          selected={itemFilter}
          onChange={setItemFilter}
        />

        <label className="pillselect">
          <span className="pillselect__label">Sort by</span>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value as ShelfSort)}>
            <option value="item">Item</option>
            <option value="store">Store</option>
            <option value="colour">Colour</option>
            <option value="size">Size</option>
          </select>
        </label>

        {/* THE ALERTS COME TO THE MANAGER RATHER THAN BEING GONE LOOKING FOR —
            across every room they run, not only the ones on screen. A threshold
            you have to visit sixteen rooms to discover is decoration.

            REC-65: as ONE CHIP that filters the table, not a box of up to eight
            rows above the filters on every visit, pushing the table down. The
            count is the server's (`stock_reorder_alerts`, every room you run);
            pressing it widens the stores to all of yours, so the table then
            holds exactly those lines. "Waiting orders" is the same idea for
            whoever is unpacking a delivery: the lines somebody is queued for.
            14 Oct: on the filter row, because they ARE filters. */}
        {(alerts.length > 0 || waitingLines > 0 || show !== 'all') && (
          <div className="chips roomstock__quick" role="group" aria-label="Show only">
            {alerts.length > 0 && (
              <button
                type="button"
                className={`chip chip--sm${show === 'low' ? ' chip--on' : ''}`}
                aria-pressed={show === 'low'}
                title="Free at or below the reorder level, in every store you run"
                onClick={() => {
                  if (show === 'low') return setShow('all')
                  setShow('low')
                  if (roomIds.length > 0) onPickRooms([])
                }}
              >
                {alerts.length} below reorder level
              </button>
            )}
            {alerts.length > 0 && <GuideLink guide="store-manager" section="reorder" label="How reorder levels work" />}
            {(waitingLines > 0 || show === 'waiting') && (
              <button
                type="button"
                className={`chip chip--sm${show === 'waiting' ? ' chip--on' : ''}`}
                aria-pressed={show === 'waiting'}
                title="Lines with orders waiting for stock"
                onClick={() => setShow(show === 'waiting' ? 'all' : 'waiting')}
              >
                {waitingLines} with orders waiting
              </button>
            )}
          </div>
        )}
      </div>

      {mine.length === 0 && (
        <p className="empty">
          You do not run a stock room yet. A shop administrator adds you to one,
          and a room is an address rather than an office picked from a list.
        </p>
      )}

      {/* NO LONGER A GATE. 075 asked for a store before it would show anything;
          076 makes an empty choice mean every store you run, so the screen opens
          on the whole picture and the picker NARROWS it (David, 15 Sep). */}
      {rows === null && !error && mine.length > 0 && (
        <p className="empty" aria-busy="true">Reading the shelves…</p>
      )}

      {rows !== null && rows.length === 0 && mine.length > 0 && (
        <p className="empty">
          Nothing on {roomIds.length === 1 ? 'that shelf' : 'any of your shelves'} yet.
          Add item stock to record what is actually in the room — or set a
          reorder level first, so it tells you the moment it runs out.
        </p>
      )}

      {/* The filtered-to-nothing case is its OWN sentence. Wiki.tsx's rule: the
          commonest confusion with a filtered list is not knowing it is
          filtered, and "nothing here" for a shelf that is full is the version
          of that which sends somebody to look for a bug. */}
      {rows !== null && rows.length > 0 && shown.length === 0 && (
        <p className="empty">
          Nothing matches those filters. {rows.length} lines are on{' '}
          {roomIds.length === 1 ? 'this shelf' : 'these shelves'}.
        </p>
      )}

      {rows !== null && shown.length > 0 && (
        <>

          {/* Its own scroll box, with the column names stuck to its top
              (REC-65): 16 stores × every combination is long, and the names
              used to scroll away. It also keeps 13 columns from widening the
              page at 375px. */}
          <div className="roomstock__scroll">
          <table className="admin__table roomstock__table">
            <thead>
              <tr>
                <th scope="col">{grouped ? 'Item / variant' : 'Item'}</th>
                {!grouped && <th scope="col">Variant</th>}
                <th scope="col">Store</th>
                <th scope="col">Size</th>
                <th scope="col">Colour</th>
                <th scope="col">Fit</th>
                <th scope="col">Other</th>
                <th scope="col">On shelf</th>
                {/* David's "ordered/assigned" is TWO numbers: assigned is a box
                    with a name on it, waiting is a name with no box (069). */}
                <th scope="col" className="roomstock__num">Assigned</th>
                <th scope="col" className="roomstock__num">Free</th>
                <th scope="col" className="roomstock__num">Waiting</th>
                <th scope="col">Reorder at</th>
                <th scope="col"><span className="sr-only">Save</span></th>
              </tr>
            </thead>
            <tbody>
              {grouped
                ? groups.map((g) => (
                    <ItemGroup
                      key={g.itemId}
                      group={g}
                      onError={onError}
                      onChanged={onChanged}
                      onMove={setMoving}
                    />
                  ))
                : shown.map((r) => (
                    <ShelfRow
                      key={`${r.stockRoomId}:${r.variantId}:${r.onHand}:${r.reorderLevel ?? 'x'}`}
                      row={r}
                      // Flat: the item name goes on the row, because there is no
                      // heading above it to carry it.
                      showItem
                      onError={onError}
                      onChanged={onChanged}
                      onMove={setMoving}
                    />
                  ))}
            </tbody>
          </table>
          </div>
          {/* 353 (HLP-50): each row's Move opens a modal, so the guide sits on
              the page under the table, never inside the dialog. */}
          <p className="roomstock__guides">
            <GuideLink guide="store-manager" section="move" label="How moving stock works" />
          </p>

          <p className="admin__hint">
            <strong>On shelf is a count, not a change.</strong> Type what is
            actually there — a “+10” is how two people counting one shelf produce
            a number neither of them saw. Assigned is written by the orders and
            is never typed. Free is the subtraction.
          </p>
        </>
      )}

      {/* MOVING BOXES IS ITS OWN ACT, so it is a quantity and not a recount:
          two counts that happen to add up are two unrelated observations in the
          log, with a window where the units are in neither room. */}
      <Dialog
        open={moving !== null}
        title="Move stock to another store"
        onClose={() => setMoving(null)}
      >
        {moving && (
          <MoveStock
            row={moving}
            rooms={mine}
            onError={onError}
            onCancel={() => setMoving(null)}
            onMoved={() => {
              setMoving(null)
              onChanged()
            }}
          />
        )}
      </Dialog>
    </div>
  )
}

/**
 * A quantity, a destination, and only what is free.
 *
 * The cap is `row.free` and **the signature is the guard** — `transferStock`
 * takes no "move everything" flag, so there is no call that can reach held
 * stock (REC-17's `clampGift` shape). The server checks it again, as it must;
 * what changes here is that the person finds out before the press.
 */
function MoveStock({
  row,
  rooms,
  onError,
  onCancel,
  onMoved,
}: {
  row: RoomInventoryRow
  rooms: StockRoom[]
  onError: (m: string | null) => void
  onCancel: () => void
  onMoved: () => void
}) {
  const elsewhere = rooms.filter((r) => r.id !== row.stockRoomId && r.isActive)
  const [to, setTo] = useState(elsewhere[0]?.id ?? '')
  const [qty, setQty] = useState(String(row.free))
  const [busy, setBusy] = useState(false)

  const n = Number(qty)
  const bad =
    !Number.isFinite(n) || n <= 0 || n > row.free
      ? `Between 1 and ${row.free} — only free stock moves.`
      : null

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (bad || !to) return
    setBusy(true)
    onError(null)
    try {
      await transferStock({
        variantId: row.variantId,
        fromRoomId: row.stockRoomId,
        toRoomId: to,
        quantity: n,
      })
      onMoved()
    } catch (err) {
      onError(messageOf(err))
    } finally {
      setBusy(false)
    }
  }

  if (elsewhere.length === 0) {
    return (
      <p className="empty">
        You only run one open store, so there is nowhere to move this to. A shop
        administrator can add you to another.
      </p>
    )
  }

  return (
    <form className="addstock" onSubmit={submit}>
      <p className="admin__hint">
        <strong>{row.itemName} · {row.variantLabel}</strong> at {row.roomName} —{' '}
        {row.free} free{row.held > 0 && `, ${row.held} already promised and staying put`}.
      </p>

      <div className="admin__fields">
        <label className="admin__field">
          <span>Move to</span>
          <select value={to} onChange={(e) => setTo(e.target.value)}>
            {elsewhere.map((r) => (
              <option key={r.id} value={r.id}>{r.name} — {r.city}</option>
            ))}
          </select>
        </label>
        <label className="admin__field">
          <span>How many</span>
          <input
            className="shopitems__count"
            type="number"
            min={1}
            max={row.free}
            value={qty}
            disabled={busy}
            onChange={(e) => setQty(e.target.value)}
          />
        </label>
      </div>

      {bad && <p className="shopitems__refusal" role="alert">{bad}</p>}

      <p className="admin__hint">
        Both counts move in one go. Anything promised to an order stays where
        the person is collecting it — and if somebody is waiting at the other
        end, this arriving is what fills their order.
      </p>

      <div className="shopitems__actions">
        <button type="submit" className="btn btn--primary btn--sm" disabled={busy || bad !== null}>
          {busy ? 'Moving…' : `Move ${Number.isFinite(n) ? n : 0}`}
        </button>
        <button type="button" className="btn btn--sm" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  )
}

/**
 * One item, then its combinations indented under it.
 *
 * A header row rather than a repeated item name down a column: with sixteen
 * rooms and twenty combinations the name repeats three hundred times, and the
 * thing that actually differs between the rows is the part hardest to find.
 */
function ItemGroup({
  group,
  onError,
  onChanged,
  onMove,
}: {
  group: { itemId: string; name: string; code: string; active: boolean; lines: RoomInventoryRow[] }
  onError: (m: string | null) => void
  onChanged: () => void
  onMove: (row: RoomInventoryRow) => void
}) {
  const free = group.lines.reduce((n, r) => n + r.free, 0)
  const held = group.lines.reduce((n, r) => n + r.held, 0)
  return (
    <>
      <tr className="roomstock__group">
        <th scope="colgroup" colSpan={6}>
          {/* Name and code side by side with a gap between them: they used to
              run together as one word ("…Travel Cupasobu-ceramic-…"). */}
          <span className="roomstock__item">
            <span className="roomstock__itemname">{group.name}</span>
            <span className="shopitems__code">{group.code}</span>
          {/* A delisted item's boxes are still real and still on the shelf
              (066's rule for the manager's read), so the row stays and says
              why nobody is buying them. */}
          {!group.active && <span className="pill pill--draft">Delisted</span>}
          </span>
        </th>
        <td colSpan={6} className="roomstock__grouptotal">
          {free} free{held > 0 && ` · ${held} assigned`} across {group.lines.length}{' '}
          {group.lines.length === 1 ? 'line' : 'lines'}
        </td>
      </tr>
      {group.lines.map((r) => (
        <ShelfRow
          // The SERVER's numbers are in the key, so both boxes remount when they
          // move underneath — two managers counting one shelf is the case this
          // table exists for, and a useState initialised once would keep showing
          // the first of them their own stale figure. The ROOM is in the key
          // too: one variant now appears once per room.
          key={`${r.stockRoomId}:${r.variantId}:${r.onHand}:${r.reorderLevel ?? 'x'}`}
          row={r}
          onError={onError}
          onChanged={onChanged}
          onMove={onMove}
        />
      ))}
    </>
  )
}

function ShelfRow({
  row,
  showItem = false,
  onError,
  onChanged,
  onMove,
}: {
  row: RoomInventoryRow
  /** Flat table: no group heading above, so the row carries the item name. */
  showItem?: boolean
  onError: (m: string | null) => void
  onChanged: () => void
  onMove: (row: RoomInventoryRow) => void
}) {
  const [count, setCount] = useState(String(row.onHand))
  // '' is a real and different value from '0' here: it CLEARS the level, which
  // means "stop watching this shelf". A level of 0 means "tell me when it is
  // empty". Collapsing them would make stopping impossible (074).
  const [level, setLevel] = useState(row.reorderLevel === null ? '' : String(row.reorderLevel))
  const [busy, setBusy] = useState<string | null>(null)

  const typed = Number(count)
  const refusal = stockRefusal(typed, row.held)
  const countChanged = count !== String(row.onHand)

  const parsedLevel = level.trim() === '' ? null : Number.parseInt(level, 10)
  const levelBad = parsedLevel !== null && (!Number.isFinite(parsedLevel) || parsedLevel < 0)
  const levelChanged = !levelBad && parsedLevel !== row.reorderLevel

  async function run(key: string, fn: () => Promise<unknown>) {
    setBusy(key)
    onError(null)
    try {
      await fn()
      onChanged()
    } catch (e) {
      onError(messageOf(e))
    } finally {
      setBusy(null)
    }
  }

  return (
    <tr className={row.needsReorder ? 'roomstock__row--low' : undefined}>
      {showItem && (
        <th scope="row">
          {row.itemName}
          {!row.itemIsActive && <span className="pill pill--draft">Delisted</span>}
        </th>
      )}
      <th scope={showItem ? undefined : 'row'} className={showItem ? undefined : 'roomstock__vname'}>
        {row.variantLabel}
        {/* A retired combination keeps its full name as of 076 — before that,
            removing an axis deleted the options and every retired row here
            collapsed to the same string. */}
        {!row.variantIsActive && <span className="pill pill--draft">Retired</span>}
      </th>
      <td>{row.roomName}</td>
      {/* A dash rather than an empty cell: blank reads as missing data, and most
          items do not vary by most axes. */}
      <td>{row.size ?? '—'}</td>
      <td>{row.colour ?? '—'}</td>
      <td>{row.fit ?? '—'}</td>
      <td>
        {row.extra.length === 0
          ? '—'
          : row.extra.map((e) => `${e.label}: ${e.value}`).join(' · ')}
      </td>
      <td>
        <input
          className="shopitems__count"
          type="number"
          min={0}
          value={count}
          aria-label={`${row.itemName} ${row.variantLabel} at ${row.roomName} on the shelf`}
          onChange={(e) => setCount(e.target.value)}
        />
      </td>
      <td className="roomstock__num">{row.held}</td>
      <td className="roomstock__num">{row.free}</td>
      <td className="roomstock__num">{row.awaiting > 0 ? row.awaiting : '—'}</td>
      <td>
        <span className="roomstock__level">
          <input
            className="shopitems__count roomstock__levelinput"
            inputMode="numeric"
            value={level}
            placeholder="Not watched"
            aria-label={`Reorder ${row.itemName} ${row.variantLabel} at ${row.roomName} when free falls to`}
            onChange={(e) => setLevel(e.target.value)}
          />
          {row.needsReorder && <span className="pill pill--draft">Low</span>}
        </span>
      </td>
      <td className="roomstock__act">
        {countChanged && (
          <>
            <button
              type="button"
              className="btn btn--xs"
              disabled={busy !== null || refusal !== null}
              onClick={() => run('count', () => setStock(row.variantId, row.stockRoomId, typed))}
            >
              {busy === 'count' ? 'Saving…' : 'Save count'}
            </button>
            {refusal && <span className="shopitems__refusal">{refusal}</span>}
          </>
        )}
        {levelChanged && (
          <button
            type="button"
            className="btn btn--xs"
            disabled={busy !== null}
            onClick={() =>
              run('level', () => setReorderLevel(row.variantId, row.stockRoomId, parsedLevel))
            }
          >
            {busy === 'level' ? 'Saving…' : parsedLevel === null ? 'Stop watching' : 'Save level'}
          </button>
        )}
        {levelBad && (
          <span className="shopitems__refusal">
            A level below zero cannot be reached. Leave it empty to stop watching.
          </span>
        )}
        {/* Only what is FREE can move (069), so a shelf holding nothing free
            offers nothing to move — and the button says why rather than
            refusing after the press (065's single-block-reason shape). */}
        {row.free > 0 && (
          <button type="button" className="btn btn--xs" onClick={() => onMove(row)}>
            Move
          </button>
        )}
      </td>
    </tr>
  )
}
